import addSeconds from 'date-fns/addSeconds';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import format from 'date-fns/format';
import parseIOS from 'date-fns/parseISO';
import {TFunction} from 'i18next';
// TODO: 컴파일 시점에 해당 국가의 locale이 포함되도록 해야한다
import {assert} from './assert';
import locale from './date/locale';
import {REGION} from '../constant';
import {IRegion} from '../type';

export function getDateDifference(
  dateLeft: string | number,
  dateRight: string | number
): {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
  totalDays: number;
} {
  const left = typeof dateLeft === 'string' ? parseDate(dateLeft) : dateLeft;
  const right =
    typeof dateRight === 'string' ? parseDate(dateRight) : dateRight;
  const totalSeconds = differenceInSeconds(left, right);
  if (totalSeconds <= 0) {
    return {
      years: 0,
      months: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalSeconds: 0,
      totalDays: 0,
    };
  } else {
    return {
      years: Math.floor(totalSeconds / SECONDS_OF_AN_YEAR),
      months: Math.floor(
        (totalSeconds % SECONDS_OF_AN_YEAR) / SECONDS_OF_A_MONTH
      ),
      days: Math.floor((totalSeconds % SECONDS_OF_A_MONTH) / SECONDS_OF_A_DAY),
      hours: Math.floor((totalSeconds % SECONDS_OF_A_DAY) / SECONDS_OF_AN_HOUR),
      minutes: Math.floor((totalSeconds % SECONDS_OF_AN_HOUR) / 60),
      seconds: totalSeconds % 60,
      totalSeconds,
      totalDays: Math.floor(totalSeconds / SECONDS_OF_A_DAY),
    };
  }
}

const SECONDS_OF_AN_YEAR = 60 * 60 * 24 * 365;
const SECONDS_OF_A_MONTH = 60 * 60 * 24 * 30;
export const SECONDS_OF_A_DAY = 60 * 60 * 24;
export const SECONDS_OF_AN_HOUR = 60 * 60 * 1;
const SECONDS_OF_A_MINUTE = 60 * 1;
export const MINUTES_OF_A_DAY = 60 * 24;
export const MINUTES_OF_AN_HOUR = 60;

export function getPeriodText(minutes: number, t: TFunction) {
  const returnTexts: string[] = [];
  let remainMinutes = minutes;
  if (remainMinutes >= MINUTES_OF_A_DAY) {
    const days = Math.floor(remainMinutes / MINUTES_OF_A_DAY);
    returnTexts.push(`${t('common_period_day', {count: days})}`);
    remainMinutes -= days * MINUTES_OF_A_DAY;
  }
  if (remainMinutes >= MINUTES_OF_AN_HOUR) {
    const hours = Math.floor(remainMinutes / MINUTES_OF_AN_HOUR);
    returnTexts.push(`${t('common_period_hour', {count: hours})}`);
    remainMinutes -= hours * MINUTES_OF_AN_HOUR;
  }
  if (remainMinutes > 0) {
    returnTexts.push(`${t('common_period_minute', {count: remainMinutes})}`);
  }
  return returnTexts.join(' ');
}

/**
 * 선물함 페이지는 날짜 정책이 다른 곳과 다름
 * https://podotree.atlassian.net/wiki/spaces/KW/pages/1410139906#2.-%EC%9D%B4%EB%B2%A4%ED%8A%B8
 * 위 링크 2-4 확인
 *
 * 에피소드 영역 대여 남은 시간 표기 (다른 곳과 다름)
 * https://podotree.atlassian.net/wiki/spaces/KW/pages/1405419915
 * 2 - (2) 확인
 *
 * 3일 초과: N d left로 표기, 시간은 ‘올림’ 으로 적용 , N일 남음
 *  - 3d 12h 가 남은 경우 4d left로 표기, 365일 1시간 남은 경우 366d 표기
 * 1일 이상 ~ 3일 이하: N d left로 표기, 시간은 ‘올림‘으로 적용, N일 남음
 *  - 2d 12h 가 남은 경우 2d left로 표기
 * 1시간 이상 ~ 1일 미만: N h left로 시간을 표기, 분은 30분 단위 ‘올림’적용,
 *  - N시간 남음
 * 1분 초과 ~ 1시간 미만: N m left로 분만 표기
 *  - N분 남음
 * 1분 이하: 1m left로 표기
 *  - 1분 남음
 */
export function getRemainTextShort(
  {
    endDate,
    now,
  }: {endDate: string | number; now: number | string; isCeilDay?: boolean},
  t: TFunction
) {
  const {totalSeconds} = getDateDifference(endDate, now);
  const secondsOfAnHalfHour = SECONDS_OF_AN_HOUR / 2;

  // 3일 초과
  if (totalSeconds > SECONDS_OF_A_DAY * 3) {
    return t('common_time_left', {
      value: `${t('common_period_day_short', {
        count: Math.ceil(totalSeconds / SECONDS_OF_A_DAY),
      })}`,
    });
    // 1일 이상 ~ 3일 이하
  } else if (
    totalSeconds >= SECONDS_OF_A_DAY &&
    totalSeconds <= SECONDS_OF_A_DAY * 3
  ) {
    return t('common_time_left', {
      value: `${t('common_period_day_short', {
        count: Math.ceil(totalSeconds / SECONDS_OF_A_DAY),
      })}`,
    });
    // 1시간 이상 ~ 1일 미만
  } else if (
    SECONDS_OF_AN_HOUR <= totalSeconds &&
    totalSeconds < SECONDS_OF_A_DAY
  ) {
    const isRound = totalSeconds % SECONDS_OF_AN_HOUR >= secondsOfAnHalfHour;

    return t('common_time_left', {
      value: `${t('common_period_hour_short', {
        count:
          Math.floor(totalSeconds / SECONDS_OF_AN_HOUR) + (isRound ? 1 : 0),
      })}`,
    });
    // 1분 초과 ~ 1시간 미만
  } else if (
    SECONDS_OF_A_MINUTE < totalSeconds &&
    totalSeconds < SECONDS_OF_AN_HOUR
  ) {
    return t('common_time_left', {
      value: `${t('common_period_minute_short', {
        count: Math.floor(totalSeconds / SECONDS_OF_A_MINUTE),
      })}`,
    });
    // 1분 이하
  } else {
    return t('common_time_left', {
      value: `${t('common_period_minute_short', {
        count: 1,
      })}`,
    });
  }
}

/**
 * 남은시간 정책 (참고: https://podotree.atlassian.net/wiki/spaces/KW/pages/1251737640/Client)
 * 남은 기간 표기 (기다무 남은 시간, 에피소드의 티켓 유효 기간 등)
 *
 * 3일 이상 = N day left로 표기, 시간은 '올림' 으로 적용  (ex 3days 12hours 가 남은 경우 4days left로 표기, 365일 1시간 남은 경우 366days 표기)
 * 3일 미만 = N day N hour left 로 날짜 + 시간으로 표기, 분은 '올림'으로 적용 (ex 2days 22 hours 30min 이 남은 경우 2days 23hours left로 표기)
 * 1일 미만 = N hour N min left로 시간 + 분으로 표기  (ex 19hour 3min )
 * 1시간 미만 = N min left로 분만 표기 (33 min)
 * 1분 이하 = 1min left(after) 로 표기
 *
 * ex)
 * 3일 6시간 = 4일
 * 2일 23시간 1분 = 3일
 * 23시간 59분 1초 = 1일
 * 23시간 59분 0초 = 23시간 59분
 * 59분 1초 = 1시간
 */
export function getRemainText(
  {
    endDate,
    now,
    isShortUnit = false,
    isOnlyFirstUnit = false,
    translationKey = 'common_time_left',
  }: {
    endDate: string | number;
    now: string | number;
    isShortUnit?: boolean;
    isOnlyFirstUnit?: boolean;
    translationKey?: string;
  },
  t: TFunction
) {
  const {hours, minutes, seconds, totalSeconds} = getDateDifference(
    endDate,
    now
  );
  if (!totalSeconds) {
    return '';
  }
  const totalDays = Math.floor(totalSeconds / SECONDS_OF_A_DAY);
  const totalMinutes = Math.floor(totalSeconds / 60);
  let ceiledRemainText: string;
  if (totalMinutes >= MIN_MINUTES_SHOW_ONLY_DAYS) {
    ceiledRemainText = getCeiledRemainText(
      {
        type: RemainTextType.OnlyDays,
        isShortUnit,
        second: totalDays,
        third: hours,
      },
      t
    );
  } else if (totalMinutes >= MIN_MINUTES_SHOW_DAYS_AND_HOURS) {
    ceiledRemainText = getCeiledRemainText(
      {
        type: RemainTextType.DaysHours,
        isShortUnit,
        first: totalDays,
        second: hours,
        third: minutes,
        isOnlyFirstUnit,
      },
      t
    );
  } else if (totalSeconds >= MIN_SECONDS_SHOW_ONE_DAY) {
    ceiledRemainText = getCeiledRemainText(
      {
        type: RemainTextType.DaysHours,
        isShortUnit,
        first: 1,
      },
      t
    );
  } else if (totalSeconds >= MIN_SECONDS_SHOW_HOURS_AND_MINUTES) {
    ceiledRemainText = getCeiledRemainText(
      {
        type: RemainTextType.HoursMinutes,
        isShortUnit,
        first: hours,
        second: minutes,
        third: seconds,
        isOnlyFirstUnit,
      },
      t
    );
  } else if (totalSeconds >= MIN_SECONDS_SHOW_ONE_HOUR) {
    ceiledRemainText = getCeiledRemainText(
      {
        type: RemainTextType.HoursMinutes,
        isShortUnit,
        first: 1,
      },
      t
    );
  } else {
    ceiledRemainText = getCeiledRemainText(
      {
        type: RemainTextType.OnlyMinutes,
        isShortUnit,
        second: minutes,
        third: seconds,
      },
      t
    );
  }
  return t(translationKey, {value: ceiledRemainText});
}

function getCeiledRemainText(
  {
    type,
    isShortUnit,
    first,
    second,
    third,
    isOnlyFirstUnit,
  }: {
    type: RemainTextType;
    isShortUnit: boolean;
    first?: number;
    second?: number;
    third?: number; // second로 반올림하기 위해 필요함
    isOnlyFirstUnit?: boolean;
  },
  t: TFunction
) {
  let ceiledFirst = first;
  let ceiledSecond = second !== undefined && !!third ? second + 1 : second;
  if (ceiledSecond === FULL_SECOND_VALUE_TYPE_MAP[type]) {
    ceiledFirst = ceiledFirst ? ceiledFirst + 1 : 1;
    ceiledSecond = 0;
  }
  const {firstKey, secondKey} = getRemainTextTypeKeys(type, isShortUnit);
  const firstText =
    ceiledFirst && firstKey ? t(firstKey, {count: ceiledFirst}) : '';
  const secondText =
    ceiledSecond && secondKey ? t(secondKey, {count: ceiledSecond}) : '';
  if (isOnlyFirstUnit) {
    return firstText;
  } else {
    return `${firstText}${firstText && secondText ? ' ' : ''}${secondText}`;
  }
}

// 2일 23시간 1분 이상 이면 'N일' 로 표기
const MIN_MINUTES_SHOW_ONLY_DAYS =
  2 * MINUTES_OF_A_DAY + 23 * MINUTES_OF_AN_HOUR + 1;
// 1일 이상 이면 'N일 M시간' 으로 표기, 만약 뒷부분이 0시간이면 'N일'로 표기
const MIN_MINUTES_SHOW_DAYS_AND_HOURS = MINUTES_OF_A_DAY;
// 23시간 59분 1초 ~ 1일 미만이면 '1일'로 표기
const MIN_SECONDS_SHOW_ONE_DAY =
  23 * SECONDS_OF_AN_HOUR + 59 * SECONDS_OF_A_MINUTE + 1;
// 1시간 이상이면 'N시간 M분'으로 표기, 만약 뒷부분이 0분이면 'N시간'으로 표기
const MIN_SECONDS_SHOW_HOURS_AND_MINUTES = SECONDS_OF_AN_HOUR;
// 59분 1초 이상 ~ 1시간 미만이면 '1시간'으로 표기
const MIN_SECONDS_SHOW_ONE_HOUR = 59 * SECONDS_OF_A_MINUTE + 1;

export enum RemainTextType {
  OnlyDays,
  DaysHours,
  HoursMinutes,
  OnlyMinutes,
}
const FULL_SECOND_VALUE_TYPE_MAP: {[key in RemainTextType]: number} = {
  [RemainTextType.OnlyDays]: -1,
  [RemainTextType.DaysHours]: 24,
  [RemainTextType.HoursMinutes]: 60,
  [RemainTextType.OnlyMinutes]: -1,
};
const REMAIN_TEXT_TYPE_KEY_MAP: {
  [key in RemainTextType]: {firstKey?: string; secondKey?: string};
} = {
  [RemainTextType.OnlyDays]: {secondKey: 'common_period_day'},
  [RemainTextType.DaysHours]: {
    firstKey: 'common_period_day',
    secondKey: 'common_period_hour',
  },
  [RemainTextType.HoursMinutes]: {
    firstKey: 'common_period_hour',
    secondKey: 'common_period_minute',
  },
  [RemainTextType.OnlyMinutes]: {secondKey: 'common_period_minute'},
};
function getRemainTextTypeKeys(type: RemainTextType, isShortUnit: boolean) {
  const keys = REMAIN_TEXT_TYPE_KEY_MAP[type];
  if (isShortUnit) {
    return {
      firstKey: `${keys.firstKey}_short`,
      secondKey: `${keys.secondKey}_short`,
    };
  } else {
    return keys;
  }
}

export function getTimeAgoText(
  {
    oldDate,
    now,
  }: {
    oldDate: string | number;
    now: string | number;
  },
  t: TFunction
) {
  const {years, months, days, hours, minutes} = getDateDifference(now, oldDate);

  if (years) {
    return `${t('common_time_year_ago', {
      count: years,
    })}`;
  } else if (months) {
    return `${t('common_time_month_ago', {
      count: months,
    })}`;
  } else if (days) {
    return `${t('common_time_day_ago', {count: days})}`;
  } else if (hours) {
    return `${t('common_time_hour_ago', {
      count: hours,
    })}`;
  } else if (minutes) {
    return `${t('common_time_minute_ago', {
      count: minutes,
    })}`;
  } else {
    return t('common_time_second_ago');
  }
}

const REGEX_DATE = /^\d{4}-\d{2}-\d{2}$/;
const REGEX_DATETIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
function _convertDatetimeToUtcFormat(param: string) {
  if (REGEX_DATE.test(param)) {
    return `${param}T00:00:00Z`;
  } else if (REGEX_DATETIME.test(param)) {
    const parts = param.split(' ');
    return `${parts[0]}T${parts[1]}Z`;
  } else {
    return param;
  }
}
/**
 * param이 문자열인 경우 다음 포맷이라고 가정한다.
 * yyyy-MM-ddTHH:mm:ssZ
 * yyyy-MM-dd HH:mm:ss
 * yyyy-MM-dd
 * 모두 UTC로 처리한다
 */
export function parseDate(param: string | number = '') {
  if (typeof param === 'number') {
    return new Date(param);
  }
  const currentDate = new Date();
  const dateString = _convertDatetimeToUtcFormat(param);
  const parsedDate = parseIOS(dateString);
  return isNaN(parsedDate.getDate()) ? currentDate : parsedDate;
}

export const MAX_DATE = parseDate('2038-01-19T12:14:07Z');
const BASE_DATE = parseDate('1980-01-01T00:00:00Z');
export function formatTime(seconds: number) {
  const helperDate = addSeconds(BASE_DATE, seconds);
  const durationFormat = seconds >= SECONDS_OF_AN_HOUR ? 'HH:mm:ss' : 'mm:ss';
  return format(helperDate, durationFormat);
}

const TIMEZONE_MAP: {[key in IRegion]: string} = {
  kor: 'Asia/Seoul',
  twn: 'Asia/Taipei',
  tha: 'Asia/Bangkok',
  idn: 'Asia/Jakarta',
};

const formatterMap = new Map<IRegion, Intl.DateTimeFormat>();
export function getTimezoneOffset(
  region: IRegion,
  dateObj = new Date('2020-01-01T00:00:00')
) {
  const timeZone = TIMEZONE_MAP[region];

  // 성능이 좋지않아 DateTimeFormat 인스턴스는 캐시해서 사용
  if (!formatterMap.has(region)) {
    formatterMap.set(
      region,
      new Intl.DateTimeFormat('en-us', {
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZone,
      })
    );
  }

  const formatter = formatterMap.get(region) as Intl.DateTimeFormat;
  const typeToPos: {
    [key in Intl.DateTimeFormatPartTypes]?: number;
  } = {
    year: 0,
    month: 1,
    day: 2,
    hour: 3,
    minute: 4,
    second: 5,
  };
  const formatted: Intl.DateTimeFormatPart[] = formatter.formatToParts(dateObj);
  const filled = [];
  for (const item of formatted) {
    const {type, value} = item;
    const pos = typeToPos[type];
    if (pos !== undefined) {
      filled[pos] = parseInt(value, 10);
    }
  }
  const [y, M, d, h, m, s] = filled;
  const utc = new Date(Date.UTC(y, M - 1, d, h, m, s));
  const offset = utc.getTime() - dateObj.getTime();
  return offset;
}

const DATE_OBJECT = new Date();
export function formatFromServerDate(
  date: string | number,
  _format: string = DEFAULT_DATETIME_FORMAT
) {
  const timestamp = _getTimestampForRegionFromUtcDate(date);
  return format(timestamp, _format, {
    locale,
  });
}

/**
 * date가 문자열이면 UTC로 인식한다
 */
export function _getTimestampForRegionFromUtcDate(
  date: number | string,
  region: IRegion = REGION
) {
  const timestamp = typeof date === 'number' ? date : getTimeStamp(date);
  // getTimezoneOffset는 minute를 반환한다
  const localOffset = DATE_OBJECT.getTimezoneOffset() * 60000;
  const targetOffset = getTimezoneOffset(region, parseDate(timestamp));
  // localOffset을 더하면 Date 객체(또는 date-fns의 format)는 항상 UTC 시간의 문자열을 반환한다
  // 원하는 시간대로 변환하기 위해 targetOffset을 또 더한다
  return timestamp + localOffset + targetOffset;
}

export function getRegionDate(
  date: number,
  region: IRegion = REGION
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
} {
  const timestamp = _getTimestampForRegionFromUtcDate(date, region);
  const regionDate = parseDate(timestamp);
  return {
    year: regionDate.getFullYear(),
    month: regionDate.getMonth() + 1,
    day: regionDate.getDate(),
    hour: regionDate.getHours(),
    minute: regionDate.getMinutes(),
    second: regionDate.getSeconds(),
    weekday: regionDate.getDay(),
  };
}

// region의 시간을 입력하면 해당 시간의 timestamp를 반환
export function getTimeStampForRegion(
  regionDateStr: string,
  region: IRegion = REGION
): number {
  assert(
    REGEX_DATE.test(regionDateStr) || REGEX_DATETIME.test(regionDateStr),
    'date는 두 가지 포맷만 지원한다'
  );
  return (
    getTimeStamp(regionDateStr) -
    getTimezoneOffset(region, parseDate(regionDateStr))
  );
}

export function getTimeStamp(date: string) {
  return parseDate(date).getTime();
}

const DATE_OF_REGION_FORMAT: {[key in IRegion]: string} = {
  kor: 'yyyy.MM.dd',
  twn: 'yyyy.MM.dd',
  tha: 'dd.MM.yyyy',
  idn: 'dd.MMM.yyyy',
};
const DATE_OF_REGION_SHORT_FORMAT: {[key in IRegion]: string} = {
  kor: 'yy.MM.dd',
  twn: 'yyyy.MM.dd',
  tha: 'dd.MM.yy',
  idn: 'dd.MMM.yy',
};
export const DEFAULT_DATE_FORMAT = DATE_OF_REGION_FORMAT[REGION];
export const SHORT_DATE_FORMAT = DATE_OF_REGION_SHORT_FORMAT[REGION];
const DEFAULT_DATETIME_FORMAT = `${DEFAULT_DATE_FORMAT} HH:mm:ss`;
export const DEFAULT_SHORT_DATETIME_FORMAT = `${SHORT_DATE_FORMAT} HH:mm:ss`;

export function getIsRecent(date: string | number, hoursAgo: number) {
  const now = Date.now();
  const timestamp = parseDate(date).getTime();
  return now <= timestamp + hoursAgo * 60 * 60 * 1000;
}

export function getIsBetween(fromDate: string, toDate: string) {
  return (
    getTimeStamp(fromDate) <= Date.now() && getTimeStamp(toDate) >= Date.now()
  );
}

export function getNowIsoString() {
  return new Date().toISOString();
}

// ex) PT48H
// date-fns에서 지원하지 않아서 직접 작성, 더 좋은 방법이 있는지 찾아보자.
export function getDuration(isoDuration: string) {
  const matches = isoDuration.match(ISO_8601_DURATION_REGEX);

  if (!matches) {
    return {
      isPositive: true,
      years: 0,
      months: 0,
      weeks: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalSeconds: 0,
      isoDuration: '',
    };
  }

  const years = (matches[2] && Number(matches[2].slice(0, -1))) || 0;
  const months = (matches[3] && Number(matches[3].slice(0, -1))) || 0;
  const weeks = (matches[4] && Number(matches[4].slice(0, -1))) || 0;
  const days = (matches[5] && Number(matches[5].slice(0, -1))) || 0;
  const hours = (matches[7] && Number(matches[7].slice(0, -1))) || 0;
  const minutes = (matches[8] && Number(matches[8].slice(0, -1))) || 0;
  const seconds = (matches[9] && Number(matches[9].slice(0, -1))) || 0;
  const totalSeconds =
    years * SECONDS_OF_AN_YEAR +
    months * SECONDS_OF_A_MONTH +
    days * SECONDS_OF_A_DAY +
    hours * SECONDS_OF_AN_HOUR +
    minutes * SECONDS_OF_A_MINUTE +
    seconds;
  return {
    isPositive: !matches[1],
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    totalSeconds,
    isoDuration,
  };
}

// 참고: https://stackoverflow.com/questions/32044846/regex-for-iso-8601-durations
const ISO_8601_DURATION_REGEX =
  /(-)?P(?!$)(\d+(?:\.\d+)?Y)?(\d+(?:\.\d+)?M)?(\d+(?:\.\d+)?W)?(\d+(?:\.\d+)?D)?(T(?=\d)(\d+(?:\.\d+)?H)?(\d+(?:\.\d+)?M)?(\d+(?:\.\d+)?S)?)?$/;

export function getIsToday(date: number, region?: IRegion) {
  const now = Date.now();
  const today = getRegionDate(now, region);
  const targetDate = getRegionDate(date, region);

  return (
    today.year === targetDate.year &&
    today.month === targetDate.month &&
    today.day === targetDate.day
  );
}

export function getNowFromServer(responseDateTime?: string) {
  return responseDateTime ? getTimeStamp(responseDateTime) : Date.now();
}
